/**
 * Color Wizard - Multi-step wizard for choosing brand colors with color theory guidance
 */

import { useState, useMemo } from 'react';
import { X, ChevronRight, ChevronLeft, Palette, Check, Sparkles, Info } from 'lucide-react';
import { useLanguage } from '../../../contexts/LanguageContext';
import {
  generateAccentOptions,
  generateThemeWithAccent,
  generatePrimaryPalette,
  isValidHexColor,
  type HarmonyType,
} from '../utils/colorGenerator';

interface ColorWizardProps {
  isOpen: boolean;
  onClose: () => void;
  onApply: (theme: Record<string, string>) => void;
  initialColor?: string;
}

type WizardStep = 1 | 2 | 3;

export function ColorWizard({ isOpen, onClose, onApply, initialColor = '#2e8b57' }: ColorWizardProps) {
  const { t } = useLanguage();
  const [step, setStep] = useState<WizardStep>(1);
  const [primaryColor, setPrimaryColor] = useState(initialColor);
  const [selectedAccentType, setSelectedAccentType] = useState<HarmonyType>('analogous');

  // Compute accent options from primary color (pure function)
  const accentOptions = useMemo(() => {
    if (!isValidHexColor(primaryColor)) return [];
    return generateAccentOptions(primaryColor);
  }, [primaryColor]);

  // Get selected accent object from options
  const selectedAccent = useMemo(() => {
    return accentOptions.find(o => o.type === selectedAccentType) || accentOptions[0] || null;
  }, [accentOptions, selectedAccentType]);

  // Compute generated theme from primary and accent (pure function)
  const generatedTheme = useMemo(() => {
    if (!selectedAccent || !isValidHexColor(primaryColor)) return {};
    return generateThemeWithAccent(primaryColor, selectedAccent);
  }, [primaryColor, selectedAccent]);

  // Compute primary palette for preview
  const primaryPalette = useMemo(() => {
    return generatePrimaryPalette(primaryColor);
  }, [primaryColor]);

  const handleNext = () => {
    if (step < 3) {
      setStep((step + 1) as WizardStep);
    }
  };

  const handleBack = () => {
    if (step > 1) {
      setStep((step - 1) as WizardStep);
    }
  };

  const handleApply = () => {
    onApply(generatedTheme);
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 sm:p-6">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/50 backdrop-blur-sm"
        onClick={onClose}
        role="button"
        tabIndex={0}
        aria-label={t('modal.close', 'Close modal')}
        onKeyDown={(e) => e.key === 'Escape' && onClose()}
      />

      {/* Modal */}
      <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-hidden flex flex-col sm:max-h-[85vh]">
        {/* Header */}
        <div className="flex items-center justify-between p-4 sm:p-6 border-b border-gray-200">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-gradient-to-br from-primary-500 to-primary-600 flex items-center justify-center">
              <Sparkles className="w-5 h-5 text-white" />
            </div>
            <div>
              <h2 className="text-lg font-bold text-gray-900">
                {t('colorWizard.title', 'Theme Wizard')}
              </h2>
              <p className="text-sm text-gray-500">
                {t('colorWizard.step', 'Step {step} of 3').replace('{step}', String(step))}
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
            aria-label={t('modal.close', 'Close')}
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Step Indicator */}
        <div className="px-4 sm:px-6 py-3 bg-gray-50 border-b border-gray-200">
          <div className="flex items-center justify-between">
            {[1, 2, 3].map((s) => (
              <div key={s} className="flex items-center flex-1">
                <div
                  className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium transition-colors ${
                    s === step
                      ? 'bg-primary-600 text-white'
                      : s < step
                      ? 'bg-green-500 text-white'
                      : 'bg-gray-200 text-gray-500'
                  }`}
                >
                  {s < step ? <Check className="w-4 h-4" /> : s}
                </div>
                {s < 3 && (
                  <div
                    className={`flex-1 h-1 mx-2 rounded ${
                      s < step ? 'bg-green-500' : 'bg-gray-200'
                    }`}
                  />
                )}
              </div>
            ))}
          </div>
          <div className="flex justify-between mt-2 text-xs text-gray-500">
            <span>{t('colorWizard.step1Label', 'Primary')}</span>
            <span>{t('colorWizard.step2Label', 'Accent')}</span>
            <span>{t('colorWizard.step3Label', 'Review')}</span>
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-4 sm:p-6">
          {/* Step 1: Choose Primary Color */}
          {step === 1 && (
            <div className="space-y-6">
              <div className="text-center">
                <Palette className="w-12 h-12 mx-auto text-primary-600 mb-3" />
                <h3 className="text-xl font-bold text-gray-900 mb-2">
                  {t('colorWizard.step1Title', 'Choose Your Primary Color')}
                </h3>
                <p className="text-gray-600 max-w-md mx-auto">
                  {t('colorWizard.step1Description', 'Your primary color is the foundation of your brand. It will be used for buttons, links, headers, and key UI elements.')}
                </p>
              </div>

              <div className="flex flex-col items-center gap-4">
                <div className="flex items-center gap-4">
                  <input
                    type="color"
                    value={primaryColor}
                    onChange={(e) => setPrimaryColor(e.target.value)}
                    className="w-20 h-20 rounded-xl cursor-pointer border-2 border-gray-200 shadow-lg"
                  />
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      {t('colorWizard.hexValue', 'Hex Value')}
                    </label>
                    <input
                      type="text"
                      value={primaryColor}
                      onChange={(e) => {
                        const val = e.target.value;
                        if (val.match(/^#?[0-9A-Fa-f]{0,6}$/)) {
                          setPrimaryColor(val.startsWith('#') ? val : `#${val}`);
                        }
                      }}
                      className="w-28 px-3 py-2 text-center font-mono border border-gray-300 rounded-lg bg-white text-gray-900 focus:ring-2 focus:ring-primary-500"
                    />
                  </div>
                </div>

                {/* Preview of generated palette */}
                <div className="w-full max-w-md">
                  <p className="text-sm text-gray-500 mb-2 text-center">
                    {t('colorWizard.palettePreview', 'Generated palette preview')}
                  </p>
                  <div className="flex rounded-lg overflow-hidden shadow-md">
                    {['primary_100', 'primary_300', 'primary_500', 'primary_600', 'primary_800'].map((key) => (
                      <div
                        key={key}
                        className="flex-1 h-12"
                        style={{ backgroundColor: primaryPalette[key] }}
                        title={`${key}: ${primaryPalette[key]}`}
                      />
                    ))}
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Step 2: Choose Accent Color */}
          {step === 2 && (
            <div className="space-y-6">
              <div className="text-center">
                <h3 className="text-xl font-bold text-gray-900 mb-2">
                  {t('colorWizard.step2Title', 'Choose Your Accent Color')}
                </h3>
                <p className="text-gray-600 max-w-md mx-auto">
                  {t('colorWizard.step2Description', 'Select a color harmony that best fits your brand personality. Each creates a different visual feel.')}
                </p>
              </div>

              {/* Primary + Accent Preview */}
              <div className="flex justify-center gap-4 mb-4">
                <div className="text-center">
                  <div
                    className="w-16 h-16 rounded-lg shadow-md border border-gray-200"
                    style={{ backgroundColor: primaryColor }}
                  />
                  <span className="text-xs text-gray-500 mt-1 block">
                    {t('colorWizard.primary', 'Primary')}
                  </span>
                </div>
                {selectedAccent && (
                  <>
                    <div className="flex items-center text-gray-300">+</div>
                    <div className="text-center">
                      <div
                        className="w-16 h-16 rounded-lg shadow-md border border-gray-200"
                        style={{ backgroundColor: selectedAccent.colors.accent }}
                      />
                      <span className="text-xs text-gray-500 mt-1 block">
                        {t('colorWizard.accent', 'Accent')}
                      </span>
                    </div>
                  </>
                )}
              </div>

              {/* Accent Options Grid */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {accentOptions.map((option) => (
                  <button
                    key={option.type}
                    onClick={() => setSelectedAccentType(option.type)}
                    className={`p-4 rounded-xl border-2 text-left transition-all ${
                      selectedAccentType === option.type
                        ? 'border-primary-500 bg-primary-50 shadow-md'
                        : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'
                    }`}
                  >
                    <div className="flex items-start gap-3">
                      {/* Color Swatches */}
                      <div className="flex-shrink-0 flex gap-1">
                        <div
                          className="w-8 h-8 rounded-lg shadow-sm border border-gray-200"
                          style={{ backgroundColor: option.colors.accent }}
                        />
                        <div
                          className="w-8 h-8 rounded-lg shadow-sm border border-gray-200"
                          style={{ backgroundColor: option.colors.accent_dark }}
                        />
                      </div>

                      {/* Text */}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <span className="font-semibold text-gray-900">{option.name}</span>
                          {selectedAccentType === option.type && (
                            <Check className="w-4 h-4 text-primary-600" />
                          )}
                        </div>
                        <p className="text-xs text-gray-500 mt-1 line-clamp-2">
                          {option.description}
                        </p>
                      </div>
                    </div>
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Step 3: Review & Apply */}
          {step === 3 && (
            <div className="space-y-6">
              <div className="text-center">
                <Check className="w-12 h-12 mx-auto text-green-500 mb-3" />
                <h3 className="text-xl font-bold text-gray-900 mb-2">
                  {t('colorWizard.step3Title', 'Review Your Theme')}
                </h3>
                <p className="text-gray-600 max-w-md mx-auto">
                  {t('colorWizard.step3Description', 'Here\'s your complete color palette. You can still adjust individual colors after applying.')}
                </p>
              </div>

              {/* Summary */}
              <div className="bg-gray-50 rounded-xl p-4 flex items-center justify-center gap-6">
                <div className="text-center">
                  <div
                    className="w-12 h-12 rounded-lg shadow-md border border-gray-200 mx-auto"
                    style={{ backgroundColor: primaryColor }}
                  />
                  <span className="text-xs text-gray-500 mt-1 block">
                    {t('colorWizard.primary', 'Primary')}
                  </span>
                </div>
                <div className="text-2xl text-gray-300">+</div>
                <div className="text-center">
                  <div
                    className="w-12 h-12 rounded-lg shadow-md border border-gray-200 mx-auto"
                    style={{ backgroundColor: selectedAccent?.colors.accent }}
                  />
                  <span className="text-xs text-gray-500 mt-1 block">
                    {selectedAccent?.name}
                  </span>
                </div>
              </div>

              {/* Full Palette Preview */}
              <div>
                <p className="text-sm font-medium text-gray-700 mb-2">
                  {t('colorWizard.primaryPalette', 'Primary Palette')}
                </p>
                <div className="flex rounded-lg overflow-hidden shadow-md mb-4">
                  {Object.entries(generatedTheme)
                    .filter(([key]) => key.startsWith('primary_'))
                    .sort(([a], [b]) => parseInt(a.split('_')[1]) - parseInt(b.split('_')[1]))
                    .map(([key, value]) => (
                      <div
                        key={key}
                        className="flex-1 h-10"
                        style={{ backgroundColor: value }}
                        title={`${key}: ${value}`}
                      />
                    ))}
                </div>

                <p className="text-sm font-medium text-gray-700 mb-2">
                  {t('colorWizard.accentAndAction', 'Accent & Action Colors')}
                </p>
                <div className="flex flex-wrap gap-2">
                  {['accent', 'accent_dark', 'action_light', 'action', 'action_dark'].map((key) => (
                    <div key={key} className="flex items-center gap-2">
                      <div
                        className="w-8 h-8 rounded-lg shadow-sm border border-gray-200"
                        style={{ backgroundColor: generatedTheme[key] }}
                      />
                      <span className="text-xs text-gray-600">{key.replace('_', ' ')}</span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Info Note */}
              <div className="flex items-start gap-3 p-4 bg-blue-50 rounded-xl border border-blue-200">
                <Info className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
                <p className="text-sm text-blue-800">
                  {t('colorWizard.applyNote', 'Clicking "Apply Theme" will add these colors to your pending changes. Remember to save to apply them to your site.')}
                </p>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between p-4 sm:p-6 border-t border-gray-200 bg-gray-50">
          <button
            onClick={step === 1 ? onClose : handleBack}
            className="flex items-center gap-2 px-4 py-2 text-gray-600 hover:bg-gray-200 rounded-lg transition-colors"
          >
            {step === 1 ? (
              t('colorWizard.cancel', 'Cancel')
            ) : (
              <>
                <ChevronLeft className="w-4 h-4" />
                {t('colorWizard.back', 'Back')}
              </>
            )}
          </button>

          {step < 3 ? (
            <button
              onClick={handleNext}
              disabled={step === 2 && accentOptions.length === 0}
              className="flex items-center gap-2 px-6 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {t('colorWizard.next', 'Next')}
              <ChevronRight className="w-4 h-4" />
            </button>
          ) : (
            <button
              onClick={handleApply}
              className="flex items-center gap-2 px-6 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
            >
              <Check className="w-4 h-4" />
              {t('colorWizard.applyTheme', 'Apply Theme')}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

export default ColorWizard;
